/**
 * Investment Items Footer Cells
 *
 * Builds a totals row for the investment items table. Aggregates the new
 * column layout: Totaal budget, Wv. strategisch, Wv. overflow, and Wv.
 * gebruikt in projecten. Mirrors the column order from
 * `createInvestmentItemCells` so columns line up via the same
 * gridTemplateColumns.
 */

import type { CellConfig } from "shared/components/UniversalTable/components";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

export const buildInvestmentFooterCells = (
  visibleRows: Array<{
    estimatedCostExclVat?: number | string | null;
    allocatedBudget?: number | string | null;
    bucket?: { id?: number; totalAmount?: number | string | null } | null;
    isReleased?: boolean;
    releasedAmount?: number | string | null;
    partAssignments?: Array<{
      strategicAmount?: number | string | null;
      overflowAmount?: number | string | null;
    }>;
  }>,
): CellConfig[] => {
  // Strategic headline: sum of each row's pre-release strategic claim
  // (`min(estimatedCostExclVat, bucket.totalAmount)`) — matches the row
  // headline. The release adjustment is surfaced separately as a subline,
  // so users see both the original commitment and what's left after
  // anything was vrijgegeven.
  let totalStrategicOriginal = 0;
  let totalReleased = 0;
  let totalOverflow = 0;
  let totalAllocated = 0;
  for (const item of visibleRows) {
    const allocated = Number(item.allocatedBudget) || 0;
    totalAllocated += allocated;

    const est = Number(item.estimatedCostExclVat) || 0;
    const potCap = item.bucket?.totalAmount
      ? Number(item.bucket.totalAmount) || 0
      : 0;
    const strategic = item.bucket?.id ? Math.min(est, potCap || est) : 0;
    totalStrategicOriginal += strategic;

    if (item.isReleased) {
      totalReleased += Number(item.releasedAmount) || 0;
    }

    const assignments = item.partAssignments ?? [];
    if (assignments.length) {
      for (const a of assignments) {
        totalOverflow += Number(a.overflowAmount) || 0;
      }
    } else {
      totalOverflow += Math.max(0, allocated - strategic);
    }
  }
  const totalStrategicAdjusted = Math.max(
    0,
    totalStrategicOriginal - totalReleased,
  );
  // totalAllocated breakdown for the footer: how much fits inside the
  // line-level detaillering vs. how much exceeds it. Avoids a single
  // ambiguous percentage; the two amounts make the over/under explicit.
  const totalAllocatedWithinDetail = Math.max(
    0,
    totalAllocated - totalOverflow,
  );

  return [
    { id: "checkbox", colSpan: 1, content: null },
    { id: "actions", colSpan: 1, content: null },
    { id: "status", colSpan: 1, content: null },
    {
      id: "title",
      colSpan: 1,
      align: "left",
      content: (
        <span className="text-sm font-semibold text-neutral-primary">
          Totaal ({visibleRows.length})
        </span>
      ),
    },
    { id: "label", colSpan: 1, content: null },
    { id: "building", colSpan: 1, hidden: true, content: null },
    { id: "units", colSpan: 1, hidden: true, content: null },
    {
      id: "strategicBudget",
      colSpan: 1,
      align: "right",
      content:
        totalStrategicOriginal > 0 ? (
          <div className="flex flex-col items-end gap-0.5">
            <span className="text-sm font-semibold text-moss-primary">
              {formatCurrency(totalStrategicOriginal)}
            </span>
            {totalReleased > 0 && (
              <>
                <span className="text-[10px] font-medium text-amber-700">
                  −{formatCurrency(totalReleased)} vrijgegeven
                </span>
                <span
                  className="text-[10px] font-semibold text-neutral-secondary-mid"
                  title="Strategisch budget na aftrek van vrijgegeven bedragen"
                >
                  {formatCurrency(totalStrategicAdjusted)} na vrijgegeven
                </span>
              </>
            )}
          </div>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        ),
    },
    {
      id: "allocatedBudget",
      colSpan: 1,
      align: "right",
      content: (
        <div
          className="flex flex-col items-end"
          title={`${formatCurrency(totalAllocatedWithinDetail)} binnen lijn-detail + ${formatCurrency(totalOverflow)} lijn-detail overschreden = ${formatCurrency(totalAllocated)} totaal vastgelegd`}
        >
          <span className="text-sm font-semibold text-moss-primary">
            {formatCurrency(totalAllocated)}
          </span>
          {totalOverflow > 0 && (
            <span className="text-[10px] text-neutral-secondary-mid">
              {formatCurrency(totalAllocatedWithinDetail)} binnen +{" "}
              <span className="text-amber-700">
                {formatCurrency(totalOverflow)} over
              </span>
            </span>
          )}
        </div>
      ),
    },
    {
      id: "overflowBudget",
      colSpan: 1,
      align: "right",
      content:
        totalOverflow > 0 ? (
          <span className="text-sm font-semibold text-amber-700">
            {formatCurrency(totalOverflow)}
          </span>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        ),
    },
    { id: "createdBy", colSpan: 1, hidden: true, content: null },
    { id: "createdAt", colSpan: 1, hidden: true, content: null },
  ];
};
